import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import SEO from '../components/SEO';
import '../styles/LegalDocument.css';

// Map route parameters to document titles and file paths
const documentMap = {
  'terms-of-service': {
    title: 'Terms of Service (CGU)',
    path: '/legal/terms_of_service.md'
  },
  'terms-and-conditions-of-sale': {
    title: 'Terms and Conditions of Sale (CGV)',
    path: '/legal/terms_and_conditions_of_sale.md'
  },
  'privacy-policy': {
    title: 'Privacy Policy',
    path: '/legal/privacy_policy.md'
  }
};

export default function LegalDocument() {
  const { documentType } = useParams();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {


    const fetchDocument = async () => {
      setLoading(true);
      try {
        const doc = documentMap[documentType];
        if (!doc) {
          setError('Document not found');
          setLoading(false);
          return;
        }

        const response = await fetch(doc.path);
        if (!response.ok) {
          throw new Error(`Failed to load document: ${response.statusText}`);
        }

        const text = await response.text();
        setContent(text);
      } catch (err) {
        console.error('Error loading legal document:', err);
        setError('Failed to load the document. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [documentType]);

  // Get document title from the map or format the URL parameter
  const documentTitle = documentMap[documentType]?.title ||
    documentType?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') ||
    'Legal Document';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950 py-8 px-4 sm:px-6 lg:px-8">
      <SEO
        title={documentTitle}
        description={`Legal document: ${documentTitle}`}
      />
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
        </div>

        <div className="bg-white dark:bg-dark-900 shadow-md rounded-lg p-6 md:p-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">
                {error}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Please try again later or contact support.
              </p>
            </div>
          ) : (
            <div className="legal-document prose prose-lg dark:prose-invert max-w-none">
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
